import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import Compressor from "compressorjs";
import _mapKeys from "lodash-es/mapKeys";
import path from "path-browserify";
// Use the prebuilt UMD bundle: it inlines all deps and avoids `require("path")`
// from `twig/src/twig.path.js`, which can't resolve under Angular's esbuild build.
import Twig from "twig/twig.min.js";
import { isEmail } from "validator";

import {
    ElementId,
    ElementOrSubElementId,
    Form,
    FormElement,
    FormElementType,
    getIdByFieldUniqueName,
    SubElementId,
} from "../model";
import { AbstractControl, UntypedFormGroup } from "@angular/forms";
import { ProspectPersonalDataToSearchWith } from "../services/lookups.service";
import { isCustomLlField, LL_FIELD_UNIQUE_IDENTIFIER } from "../constants/transcription-fields";
import { ItemData } from "../model/badge";

@Injectable({ providedIn: "root" })
export class Util {
    public static elementScroll(id: string, options: ScrollIntoViewOptions): void {
        if (id) {
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView(options);
            }
        }
    }

    public static replaceStringTokens(str: string, obj: Record<any, any>): string {
        return str.replace(/{([^{}]*)}/g, (a, b) => {
            const r = obj[b];
            return r ? r : a;
        });
    }

    public static replaceTemplateStrings(template: string, obj: Record<string, any>): string {
        if (typeof obj !== "object" || obj === null || typeof template !== "string") {
            return template;
        }
        let result = template;
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
                result = result.replace(regex, obj[key]);
            }
        }
        return result;
    }

    // Renders a user-authored Twig template (e.g. merge-token strings). Missing
    // keys resolve to "" (Twig default). On parse/render failure (malformed
    // template), returns the raw template untouched so authors see their input
    // as-is.
    public static renderTwigTemplate(template: string, values: Record<string, unknown>): string {
        try {
            return Twig.twig({ data: template }).render(values) as string;
        } catch {
            return template;
        }
    }

    public static getFileExtension(fileName: string): string {
        return path.extname(fileName).replace(/\./g, "");
    }

    public static isHttpUrl(s: string): boolean {
        return s.startsWith("http://") || s.startsWith("https://");
    }

    // Rejects data URIs and long pure-base64 blobs so signature/image payloads don't
    // bleed into plain-text contexts (e.g., merge-token substitution).
    public static looksLikeBase64(s: string): boolean {
        if (s.startsWith("data:") && s.includes(";base64,")) {
            return true;
        }
        return s.length > 200 && /^[A-Za-z0-9+/]+={0,2}$/.test(s);
    }

    // Joins items with Oxford-comma style: ["a"] → "a", ["a","b"] → "a and b",
    // ["a","b","c"] → "a, b, and c".
    public static joinWithOxfordComma(items: (string | number)[], translate: TranslateService): string {
        const and = translate.instant("general.and");
        if (items.length === 0) {
            return "";
        }
        if (items.length === 1) {
            return String(items[0]);
        }
        if (items.length === 2) {
            return `${items[0]} ${and} ${items[1]}`;
        }
        return `${items.slice(0, -1).join(", ")}, ${and} ${items[items.length - 1]}`;
    }

    public static isImageFormat(ext: string): boolean {
        return ["jpg", "png", "gif", "jpeg", "svg"].includes(ext.toLowerCase());
    }

    public static hexToRgb(hex: string): string {
        // Remove the '#' character if it exists
        hex = hex.replace("#", "");

        // Check if the remaining string is a valid hex code
        if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
            // Return black if the code is invalid
            return "0,0,0";
        }

        // Convert the hex values to decimal
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        // Return the RGB color as a string
        return `${r},${g},${b}`;
    }

    public static convertYearToDays(yrs: number): number {
        return Math.floor(yrs * 365.25);
    }

    public static parseJson(data: any): any {
        if (typeof data != "string") return data;
        try {
            return JSON.parse(data);
        } catch (_) {
            return data;
        }
    }

    public static convertTo24Hour(time: string): string {
        time = time.toLowerCase();
        const hoursBeforeParsing = time.substr(0, 2);
        const hours = parseInt(hoursBeforeParsing);
        if (time.indexOf("am") != -1 && hours == 12) {
            time = time.replace("12", "00");
        }
        if (time.indexOf("pm") != -1 && hours < 12) {
            time = time.replace(hoursBeforeParsing, String(hours + 12));
        }
        return time.replace(/(am|pm)/, "").trim();
    }

    public static addUserTimezone(time: string): string {
        try {
            const timeDifference = new Date(time).toString().split("GMT")[1];
            return time.split("+")[0] + timeDifference.slice(0, 3) + ":" + timeDifference.slice(3, 5);
        } catch (e) {
            console.error(e);
            return time;
        }
    }

    public static safeParse(data: any): any {
        try {
            return JSON.parse(data);
        } catch (_) {
            return "";
        }
    }

    public static blobToBase64(blob: any): Promise<string | ArrayBuffer> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (reader.result === null) {
                    reject(new Error("Failed to read blob"));
                } else {
                    resolve(reader.result);
                }
            };
            reader.readAsDataURL(blob);
        });
    }

    public static getQueryStrings(snapshot: ActivatedRouteSnapshot, startWith?: string): Record<string, string> {
        const params = {};
        for (const key in snapshot.queryParams) {
            if (!startWith || key.startsWith(startWith)) {
                params[key] = snapshot.queryParams[key];
            }
        }
        return params;
    }

    public static compressAsync(file: File | Blob, quality: number): Promise<File | Blob> {
        return new Promise((resolve, reject) => {
            new Compressor(file, {
                quality: quality,
                success(result) {
                    resolve(result);
                },
                error(err) {
                    reject(err.message);
                },
            });
        });
    }

    static isArrayOfString(obj: any): obj is string[] {
        return Array.isArray(obj) && obj.every((el) => typeof el === "string");
    }

    public static getFullName(firstName: string | undefined, lastName: string | undefined): string {
        return (firstName ? firstName : "") + (lastName ? " " + lastName : "");
    }

    public static validateEmail(email: string): boolean {
        return isEmail(email);
    }

    public static extractEmailDomain(email: string): string {
        const atIndex = email.lastIndexOf("@");
        if (atIndex === -1) {
            return "";
        }
        return email
            .slice(atIndex + 1)
            .trim()
            .toLowerCase();
    }

    /** Correlation id for host request/response messages (e.g. transcribe_scanner_request). */
    public static generateRequestId(): string {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }

    public static customPostMessage(message: string, origin: string = "*"): void {
        /*
         the mobile app will create a channel called eventGen,
         don't add * to the postMessage when sending to the mobile channel
         because it won't send it
         */

        // @ts-ignore
        if (typeof eventGen !== "undefined") {
            // @ts-ignore
            eventGen.postMessage(message);
            return;
        }

        window.parent.postMessage(message, origin);
    }

    /**
     * If the id is already a parent id, it will return it
     * @example
     *   // For ID: "element_1_sub_1_2", it will return "element_1_sub_1"
     *   // For ID: "element_2_5", it will return "element_2"
     *   // For ID: "element_2", it will return "element_2"
     */
    public static getParentId(id: ElementOrSubElementId): ElementId {
        if (!this.isSubElementId(id)) return id;
        return id.split("_").slice(0, -1).join("_") as ElementId;
    }

    /** Numeric form-element id from strings like `element_123` or sub-element ids like `element_123_1`. */
    public static getElementNumericId(id: ElementOrSubElementId): number {
        return Number(Util.getParentId(id).split("_")[1]);
    }

    /**
     * @example
     *   // For ID: "element_1", it will return true
     *   // For ID: "element_1_sub_1", it will return true
     */
    public static isParentId(id: string): id is ElementId {
        return /^element_\d+(?:_[a-zA-Z]+_\d+)?$/.test(id);
    }

    /**
     * @example
     *   // For ID: "element_1_2", it will return true
     *   // For ID: "element_1_sub_1_2", it will return true
     */
    public static isSubElementId(id: string): id is SubElementId {
        return /^element_\d+_(\w+_\d+_)?\d+$/.test(id);
    }

    public static appendEmailToDestinationUrl(sourceUrl: string, destinationUrl: string): string {
        const URL_1 = new URL(sourceUrl);
        const URL_2 = new URL(destinationUrl);
        if (URL_1.hostname === URL_2.hostname) {
            if (URL_1.hostname.endsWith("capture.captello.com")) {
                const emailParamValue = URL_1.searchParams.get("email") || URL_1.searchParams.get("eml");
                if (emailParamValue !== null && emailParamValue !== "") {
                    const paramKey = URL_1.searchParams.get("email") ? "email" : "eml";
                    URL_2.searchParams.append(paramKey, emailParamValue);
                }
            }
        }
        return URL_2.toString();
    }

    public static processUrlThenRedirect(url: string): void {
        const destinationUrl = this.appendEmailToDestinationUrl(window.location.href, url);
        window.open(destinationUrl, "_self");
    }

    public static isMatchAllSearchTerms(sentence: string, searchText: string) {
        return searchText.split(" ").every((word) => sentence.toLowerCase().includes(word.toLowerCase()));
    }

    public static convertMbToBytes(sizeMb: number): number {
        return sizeMb * 1024 * 1024;
    }

    public static getProspectPersonalDataToSearchWith(
        form: Form,
        formGroup: UntypedFormGroup,
    ): ProspectPersonalDataToSearchWith {
        const transcriptionFieldsForManualSearch: LL_FIELD_UNIQUE_IDENTIFIER[] = [
            "FirstName",
            "LastName",
            "Company",
            "JobTitle",
            "Email",
        ];
        const [firstName, lastName, company, jobTitle, email] = transcriptionFieldsForManualSearch
            .map((identifier) => getIdByFieldUniqueName(identifier, form.elements))
            .map((id) => {
                if (!id) {
                    return "";
                }
                if (Util.isSubElementId(id)) {
                    const parentId = Util.getParentId(id);
                    return formGroup.get(parentId)?.get(id)?.value || "";
                }
                return formGroup.get(id)?.value || "";
            });
        return { firstName, lastName, company, jobTitle, email };
    }

    /**
     * All standard (non-custom) LL mappings on the form, with current form-group values.
     * Parent/sub-element values (e.g. FirstName/LastName) are read from flattened form-group keys.
     */
    public static getStandardMappedFieldsWithValues(form: Form, formGroup: UntypedFormGroup): ItemData[] {
        const formValues = Form.flattenFormGroupValue(form, formGroup);
        const fields: ItemData[] = [];

        for (const element of form.elements) {
            for (const mapping of element.mapping) {
                if (isCustomLlField(mapping)) {
                    continue;
                }

                const id = "sub_element_id" in mapping && mapping.sub_element_id ? mapping.sub_element_id : element.id;

                fields.push({
                    ll_field_unique_identifier: mapping.ll_field_unique_identifier,
                    ll_field_id: mapping.ll_field_id,
                    value: Util.getMappedFieldValue(id, formValues, formGroup),
                });
            }
        }

        return fields;
    }

    private static getMappedFieldValue(
        id: ElementOrSubElementId,
        formValues: Record<string, unknown>,
        formGroup: UntypedFormGroup,
    ): string {
        for (const candidate of [formValues[id], Util.getFormGroupValueByElementId(formGroup, id)]) {
            if (candidate != null && String(candidate).trim() !== "") {
                return String(candidate).trim();
            }
        }
        return "";
    }

    public static getFormGroupValueByElementId(formGroup: UntypedFormGroup, id: ElementOrSubElementId): string {
        if (!Util.isSubElementId(id)) {
            return String((formGroup.controls[id] ?? formGroup.get(id))?.value ?? "").trim();
        }

        const parentId = Util.getParentId(id);
        const parentControl = formGroup.controls[parentId] ?? formGroup.get(parentId);
        if (!parentControl) {
            return "";
        }

        const nestedGroup = parentControl as UntypedFormGroup;
        const subControl = nestedGroup.controls?.[id] ?? nestedGroup.get?.(id);
        if (subControl) {
            return String(subControl.value ?? "").trim();
        }

        const raw =
            typeof nestedGroup.getRawValue === "function"
                ? (nestedGroup.getRawValue() as Record<string, unknown>)
                : null;
        if (raw?.[id] != null && String(raw[id]).trim() !== "") {
            return String(raw[id]).trim();
        }

        const parentValue = parentControl.value;
        if (parentValue && typeof parentValue === "object" && !Array.isArray(parentValue)) {
            return String((parentValue as Record<string, unknown>)[id] ?? "").trim();
        }
        return "";
    }

    // `controlPath` (not `path`) to avoid shadowing the module-level `path-browserify` import.
    static safeGetNestedControl(parentControl: AbstractControl, controlPath: string) {
        if (!parentControl) {
            console.log("you can't use this element until you create the form group");
        }
        const childControl = parentControl.get(controlPath);
        if (!childControl) {
            console.log("you can't use this element until you create the form group for it");
        }
        return childControl;
    }

    public static parseTimestamp(value: string | number | null | undefined): Date | null {
        if (value == null || value === "") {
            return null;
        }

        const numeric = typeof value === "number" ? value : Number(value);
        if (!Number.isFinite(numeric)) {
            return null;
        }

        const milliseconds = numeric < 1_000_000_000_000 ? numeric * 1000 : numeric;
        return new Date(milliseconds);
    }

    public static formatAmount(value: string | number | null | undefined, short = false): string {
        if (value == null || value === "") {
            return "";
        }

        const raw = String(value).trim();
        const cleaned = raw.replace(/[^0-9.-]+/g, "");
        if (!/\d/.test(cleaned)) {
            return raw;
        }

        const numeric = Number(cleaned);
        if (!Number.isFinite(numeric)) {
            return raw;
        }

        if (short) {
            return Util.formatShortAmount(numeric);
        }

        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(numeric);
    }

    private static formatShortAmount(numeric: number): string {
        const abs = Math.abs(numeric);
        const sign = numeric < 0 ? "-" : "";

        let scaled: number;
        let suffix: string;

        if (abs >= 1_000_000_000) {
            scaled = abs / 1_000_000_000;
            suffix = "B";
        } else if (abs >= 1_000_000) {
            scaled = abs / 1_000_000;
            suffix = "M";
        } else if (abs >= 1_000) {
            scaled = abs / 1_000;
            suffix = "K";
        } else {
            return new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            }).format(numeric);
        }

        const formatted = scaled % 1 === 0 ? String(scaled) : scaled.toFixed(1).replace(/\.0$/, "");

        return `${sign}$${formatted}${suffix}`;
    }

    // Returns a shallow copy of `obj` with every key lower-cased. Used so predefined
    // merge-token values (e.g. {firstName, jobTitle, ...}) can be looked up with a single,
    // predictable lowercase key regardless of source casing.
    public static lowerCaseObjectKeys<T>(obj: Record<string, T>): Record<string, T> {
        return _mapKeys(obj, (_value, key) => key.toLowerCase());
    }

    // Rewrites {{TokenName}} occurrences (any casing, e.g. {{FirstName}}) to their canonical
    // lowercase form so Twig's case-sensitive lookup resolves them regardless of how the
    // author typed the token in a merge-token template (e.g. a button's url).
    public static normalizeMergeTokenCasing(template: string, tokenNames: string[]): string {
        return tokenNames.reduce((result, name) => {
            const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            return result.replace(new RegExp(`{{\\s*${escapedName}\\s*}}`, "gi"), `{{${name}}}`);
        }, template);
    }

    // Percent-encodes every value in `obj` so it's safe to substitute into a URL template
    // (e.g. a value containing "&" or "=" won't corrupt the query string it's inserted into).
    public static urlEncodeObjectValues(obj: Record<string, string>): Record<string, string> {
        return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, encodeURIComponent(value)]));
    }

    // An intelligence-CRM element is only renderable once the builder has picked a CRM source:
    // without an alias there is no icon/label to show and no source to enrich against.
    public static hasIntelligenceCrmSource(element: FormElement): boolean {
        return element.type === FormElementType.intelligence_crm && !!element.crm_source?.alias;
    }
}
